import type { Container } from "pixi.js";
import type { AnticipationBackgroundTuning } from "../tuning/anticipationBackgroundTuning";
import { ColorOverlayFilter } from "@slot/presentation";
import type { PresentationTaskRunner } from "@slot/presentation";

/**
 * Game-side anticipation BACKGROUND overlay. The reel mechanic reports the anticipation
 * SESSION through its `anticipationSessionListener` option; createPresentation wires onStart(ordinal) →
 * noteReelStart(ordinal) and onEnd → rampOut(). The overlay starts only from the `startFromReel`-th
 * anticipated reel. It creeps each target sprite's soft-light overlay `strength` 0 → maxStrength
 * while the tease runs, then snaps it back — the background "lights up" with the anticipation.
 *
 * The ramp rides the CHROME runner (NOT clock-scaled), so it still
 * pauses / frame-steps but never speed-scales. Filters are attached only while the strength is
 * non-zero, so the background costs nothing at rest.
 */
export class AnticipationBackground {
  private readonly overlayTargets: Container[];
  private readonly runner: PresentationTaskRunner;
  private readonly filters = new WeakMap<Container, ColorOverlayFilter>();
  private tuning: AnticipationBackgroundTuning | null = null;
  private strength = 0;
  private generation = 0;

  public constructor(input: { overlayTargets: Container[]; chromeRunner: PresentationTaskRunner }) {
    this.overlayTargets = input.overlayTargets;
    this.runner = input.chromeRunner;
  }

  public setTuning(tuning: AnticipationBackgroundTuning): void {
    this.tuning = tuning;
  }

  /** A flagged reel entered its beat (1-based `ordinal`). Ramp in ONCE, at `startFromReel`. */
  public noteReelStart(ordinal: number): void {
    if (this.tuning && ordinal === this.tuning.startFromReel) void this.rampIn();
  }

  /** Creep the overlay strength up toward maxStrength (the tease build). */
  private async rampIn(): Promise<void> {
    const t = this.tuning;
    if (!t) return;
    const gen = ++this.generation;
    if (t.preDelaySeconds > 0) {
      await this.runner.sleep(t.preDelaySeconds);
      if (gen !== this.generation) return;
    }
    const cap = Math.max(0, t.maxStrength);
    const from = this.strength;
    const seconds = Math.max(0.001, (cap - from) / Math.max(0.0001, t.creepPerSecond));
    await this.runner.tweenProgress(seconds, (p) => {
      if (gen === this.generation) this.apply(from + (cap - from) * p);
    }, undefined, { easing: "linear" });
  }

  /** Session ended (or teardown) — hold at peak for `downStartDelaySeconds`, then snap back to 0. */
  public rampOut(): void {
    void this.runRampOut();
  }

  private async runRampOut(): Promise<void> {
    const t = this.tuning;
    if (!t) return;
    const gen = ++this.generation;
    if (t.downStartDelaySeconds > 0) {
      await this.runner.sleep(t.downStartDelaySeconds);
      if (gen !== this.generation) return;
    }
    const from = this.strength;
    await this.runner.tweenProgress(Math.max(0.001, t.downSeconds), (p) => {
      if (gen === this.generation) this.apply(from * (1 - p));
    });
    if (gen === this.generation) this.apply(0);
  }

  private apply(v: number): void {
    this.strength = v;
    for (const target of this.overlayTargets) {
      if (target.destroyed) continue;
      let filter = this.filters.get(target);
      if (v <= 0) {
        if (filter) target.filters = (target.filters ?? []).filter((f) => f !== filter);
        continue;
      }
      if (!filter) {
        filter = new ColorOverlayFilter({ mode: "softLight", strength: 0 });
        this.filters.set(target, filter);
      }
      if (!target.filters?.includes(filter)) target.filters = [...(target.filters ?? []), filter];
      filter.strength = v;
    }
  }
}
